import { Widget } from '../widget/widget.js';
import { ApiClient, ApiClientFactory } from '../api/apiclientfactory.js';

type UploadValue = {
    filename: string;
    no_change: boolean
    uploaded_file?: string;
    path?: string;
}

export class FileUploadWidget extends Widget {
    public api: ApiClient;
    public path: string;
    public maxSize = 0;
    public allowedExtensions: string[] = [];
    public uploadedFile: any;

    constructor($node: HTMLElement, args: Record<string, unknown> = {}) {
        super($node, args);
        this.$inputContainer = this.$node;

        let apiUrl: string = null;
        if (args.hasOwnProperty('apiUrl')) {
            apiUrl = args.apiUrl as string;
        }

        this.api = ApiClientFactory(apiUrl);
        this.path = $node.dataset.uploadPath;
        this.uploadedFile = null;
        if (typeof $node.dataset.maxSize !== 'undefined') {
            this.maxSize = parseInt($node.dataset.maxSize);
        }
        if (typeof $node.dataset.allowedExtensions !== 'undefined') {
            this.allowedExtensions = $node.dataset.allowedExtensions.split(',');
            this.allowedExtensions = this.allowedExtensions.map(extension => extension.trim().replace(/^\./, ''));
        }

        const $filenameNode = this.$node.querySelector('.file-name') as HTMLElement;
        const $deleteButton = this.$node.querySelector('.clear') as HTMLInputElement;

        // show chosen file name
        (this.$input as HTMLInputElement).addEventListener('change', () => {
            $filenameNode.innerHTML = (this.$input as HTMLInputElement).files[0].name;
            $filenameNode.dataset.fileName = (this.$input as HTMLInputElement).files[0].name;
            $deleteButton.disabled = false;
        });

        // reset button logic
        $deleteButton.addEventListener('click', e => {
            e.preventDefault();
            this.$input.value = '';
            $filenameNode.innerHTML = $filenameNode.dataset.noFile;
            $filenameNode.dataset.fileName = '';

            const preview = this.$node.querySelector('.file-preview');
            if (preview !== null) {
                preview.remove();
            }
        });
    }

    async validate(): Promise<boolean> {
        this.clearError();

        if (this.required === true && (this.$input as HTMLInputElement).files.length === 0) {
            this.displayError('widget.file_no_upload');
            return false;
        }

        if (this.maxSize > 0 && (this.$input as HTMLInputElement).files.length > 0 && (this.$input as HTMLInputElement).files[0].size > this.maxSize) {
            this.displayError('widget.file_too_big');
            return false;
        }

        if (this.allowedExtensions.length > 0 && (this.$input as HTMLInputElement).files.length > 0 && !this.checkFileTypeValidity()) {
            this.displayError('widget.file_extension_not_allowed', null, this.allowedExtensions.join(', '));
            return false;
        }
        return true;
    }

    async upload(): Promise<boolean> {
        // skip upload if no files to upload
        if (this.uploadedFile === null && (this.$input as HTMLInputElement).files.length === 0) {
            return true;
        }

        // upload file
        const uploadResponse = await this.api.upload.file(this.$input as HTMLInputElement, this.path);
        if (uploadResponse.statusCode === 200) {
            this.uploadedFile = uploadResponse.data.filename;
            return true;
        }
        return false;
    }

    async preUploadTask(): Promise<boolean> {
        return this.upload();
    }

    async getValue(): Promise<UploadValue> {
        if ((this.$input as HTMLInputElement).files.length > 0) {
            return {
                uploaded_file: this.uploadedFile,
                filename: (this.$input as HTMLInputElement).files[0].name,
                no_change: false,
                path: this.path,
            };
        }

        const filenameNode: HTMLElement = this.$node.getElementsByClassName('file-name')[0] as HTMLElement;

        return {
            no_change: true,
            filename: filenameNode.dataset.fileName,
        };
    }

    /**
     * Check if the file type is valid based on the allowed extensions.
     * Also supports mime types, including wildcards, e.g. image/*.
     */
    checkFileTypeValidity(): boolean {
        const fileExtension = (this.$input as HTMLInputElement).files[0].name.split('.').pop();
        if (this.allowedExtensions.includes(`${fileExtension}`)) {
            return true;
        }

        const mimeType = (this.$input as HTMLInputElement).files[0].type;

        for (const extension of this.allowedExtensions) {
            if (mimeType.includes(extension)) {
                return true;
            }

            if (extension.includes('/')) {
                const [type, subtype] = extension.split('/');
                if (subtype === '*' && mimeType.startsWith(type)) {
                    return true;
                }
            }
        }

        return false;
    }
}
